import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getStoredAgriTourDraftToken,
  loadAgriTourDraft,
  setStoredAgriTourDraftToken,
} from '@/lib/api/agritour-draft';
import { AGRITOUR_DRAFT_COPY } from './agritour-form-copy';
import type { AgriTourFormData } from './AgriTourScreen';

type UseAgriTourDraftResumeArgs = {
  onResume: (
    answers: Partial<AgriTourFormData>,
    stepIndex: number,
    draftToken: string,
  ) => void;
};

export function useAgriTourDraftResume({ onResume }: UseAgriTourDraftResumeArgs) {
  const [searchParams] = useSearchParams();
  const draftParam = searchParams.get('draft')?.trim() ?? '';
  const [loading, setLoading] = useState(() => Boolean(draftParam));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!draftParam) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const draft = await loadAgriTourDraft(draftParam);
        if (cancelled) return;

        setStoredAgriTourDraftToken(draft.draftToken);
        onResume(
          draft.answers as Partial<AgriTourFormData>,
          draft.stepIndex,
          draft.draftToken,
        );
      } catch {
        if (!cancelled) {
          setError(AGRITOUR_DRAFT_COPY.loadError);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [draftParam, onResume]);

  return { loadingDraft: loading, draftLoadError: error };
}

export function useAgriTourDraftToken() {
  const [draftToken, setDraftTokenState] = useState<string | null>(() =>
    getStoredAgriTourDraftToken(),
  );

  const setDraftToken = useCallback((token: string | null) => {
    setStoredAgriTourDraftToken(token);
    setDraftTokenState(token);
  }, []);

  return { draftToken, setDraftToken };
}

export function canSaveAgriTourDraft(formData: AgriTourFormData): boolean {
  return Boolean(formData.applicantName?.trim() || formData.ventureName?.trim());
}
